import { useState, useContext, useEffect, useMemo } from "react";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import { PERMISSIONS } from "constants/portal";
import { PermissionsContext } from "context/AppProvider/PermissionsContext";
import { useAuth0 } from "@auth0/auth0-react";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import DataTable from "cxfcomponents/Tables/DataTable";
import { ThemedMailIcon } from "assets/CXFIcons/Static";
import SoftTypography from "components/SoftTypography";
import StatusBadge from "cxfcomponents/StatusBadge";
import { UserRole } from "./user/role";
import ShareUnshareDialog from "cxfcomponents/ShareUnshareDialog";
import { insertSharedIntegration } from "api/Integrations";
import { useIntegrations } from "hooks/useIntegrations";
import { setOpenShareUnshareIntegrationDialog } from "redux/actions/portal/integrationActionCreator";
import { useDispatch } from "react-redux";
import { RemoveUserDialog } from "../../removeUser";
import { deleteSharedIntegrations } from "api/Integrations";
import { useResourceListener } from "hooks/useResourceListener";
import {
	setExternalUsersShareDialogOpen,
	setShareItemData
} from "redux/actions/portal/sharingActionCreator";

export const UsersTab = ({ integration }) => {
	const [searchName, setSearchName] = useState("");
	const { users } = useSelector((store) => store.portal.users);
	const { userPermissions } = useContext(PermissionsContext);
	const { user } = useAuth0();
	const { sharedIntegrations } = useSelector(
		(store) => store.portal.integrations
	);
	const { openShareUnshareDialog } = useSelector(
		(store) => store.portal.integrations
	);
	const [availableShareUsers, setAvailableShareUsers] = useState([]);
	const { handleFetchSharedIntegrations } = useIntegrations();
	const dispatch = useDispatch();
	const [userToBeRemoved, setUserToBeRemoved] = useState(null);
	useResourceListener(
		"integrationsSharingUpdate",
		handleFetchSharedIntegrations
	);

	const isAllowedToShare = () => {
		return userPermissions.some(
			(permission) =>
				permission.name === PERMISSIONS.SHARE_ANY_INTEGRATIONS &&
				permission.value
		);
	};

	const isAllowedToEdit = () => {
		return userPermissions.some(
			(permission) =>
				permission.name === PERMISSIONS.EDIT_ANY_INTEGRATIONS &&
				permission.value
		);
	};

	const handleAddUsers = () => {
		if (isAllowedToShare()) {
			dispatch(setOpenShareUnshareIntegrationDialog(true));
		}
	};

	const isAllowedAction = (action) => {
		switch (action) {
			case "share": {
				const sharePermissions = [PERMISSIONS.SHARE_ANY_INTEGRATIONS];

				return sharePermissions.some((permission) =>
					userPermissions.some(
						(userPermission) =>
							userPermission.name === permission && userPermission.value
					)
				);
			}
		}
		return false;
	};

	const getUsers = () => {
		if (!integration || !users) return;

		const filteredUsers = users.filter((user) => {
			if (!searchName) return true;

			return (
				user.first_name.toLowerCase().includes(searchName) ||
				user.last_name.toLowerCase().includes(searchName) ||
				user.email_address.toLowerCase().includes(searchName)
			);
		});

		let owner = filteredUsers.find(
			(user) => user.auth0_id === integration.create_user_id
		);

		if (owner) {
			owner.isOwner = true;
		}

		const usersShareWithIntegrations = sharedIntegrations.filter(
			(sharedIntegration) =>
				sharedIntegration.integration_id === integration.id
		);
		const sharedUsers = filteredUsers.filter((user) =>
			usersShareWithIntegrations.find(
				(sharedUser) => sharedUser.shared_user_id === user.auth0_id
			)
		);

		const combinedUsers = [].concat(owner ? owner : [], sharedUsers);

		return combinedUsers;
	};

	const handleCloseShareUnshareIntegrationDialog = () => {
		dispatch(setOpenShareUnshareIntegrationDialog(false));
	};

	const handleSetUserToBeRemoved = (user) => {
		setUserToBeRemoved(user);
	};

	const tableData = useMemo(() => {
		return {
			columns: [
				{
					Header: "profile",
					accessor: "profile",
					canGroupBy: false,
					disableSortBy: true,
					Cell: ({ value }) => (
						<SoftBox
							component="img"
							src={value}
							sx={{
								border: ".5px solid white",
								borderRadius: "12px",
								width: "60px",
								objectFit: "cover",
								height: "60px",
								marginRight: "10px"
							}}
						/>
					)
				},
				{
					Header: "name",
					accessor: "name",
					width: 250,
					canGroupBy: false,
					Cell: ({ value }) => (
						<SoftBox
							className="white-to-dark"
							fontSize="14px"
							fontWeight="bold"
							display="flex"
							alignItems="center"
							sx={{
								minWidth: "150px",
								maxWidth: "200px",
								whiteSpace: "normal",
								wordBreak: "break-all"
							}}
						>
							{value}
						</SoftBox>
					)
				},
				{
					Header: "email",
					accessor: "email",
					canGroupBy: false,
					Cell: ({ value }) => {
						return (
							<SoftBox>
								<SoftTypography
									color="secondary"
									sx={{
										fontWeight: 400,
										fontSize: "14px",
										display: "flex",
										alignItems: "center"
									}}
								>
									<ThemedMailIcon
										width="19px"
										height="19px"
										sx={{ marginRight: "8px" }}
									/>
									{value}
								</SoftTypography>
							</SoftBox>
						);
					}
				},
				{
					Header: "status",
					accessor: "status",
					canGroupBy: false,
					Cell: ({ value }) => {
						return (
							<SoftBox>
								<StatusBadge status={value} />
							</SoftBox>
						);
					}
				},
				{
					Header: "role",
					accessor: "user",
					canGroupBy: false,
					Cell: ({ value }) => {
						return (
							<UserRole
								user={value}
								handleSetUserToBeRemoved={handleSetUserToBeRemoved}
								isAllowedToEdit={isAllowedToEdit()}
							/>
						);
					}
				}
			].map((column) => ({ ...column, disableResize: true })),
			rows: getUsers().map((user) => {
				return {
					profile: user.image,
					name: `${user.first_name ?? ""} ${user.last_name ?? ""}`,
					email: user.email_address,
					status: user.status,
					user
				};
			})
		};
	}, [getUsers(), searchName]);

	const handleShareIntegration = async (selectedUsers, setSelectedUsers) => {
		if (!isAllowedToEdit()) return;

		const shareIntegrationInput = selectedUsers.map((selectedUser) => ({
			...selectedUser,
			integration_id: integration.id,
			tenant_id: user?.stripeId,
			owner_id: user?.cxfUserId,
			shared_tenant_id: user?.stripeId
		}));

		const shareIntegrationResponse = await insertSharedIntegration(
			user?.stripeId,
			shareIntegrationInput
		);

		if (shareIntegrationResponse?.isSuccessful) {
			toast.success(
				`Integration ${integration.integration_name} has been shared successfully!`
			);
		} else {
			toast.error(
				`Integration ${integration.integration_name} has been shared successfully!`
			);
		}

		dispatch(setOpenShareUnshareIntegrationDialog(false));
		setSelectedUsers([]);
	};

	const handleOpenExternalUsersShareDialog = () => {
		const shareItemDataData = {
			item: "Integration",
			item_id: integration.id,
			item_name: integration.integration_name,
			item_tenant_id: integration.tenant_id,
			item_owner_id: integration.create_user_id
		};
		dispatch(setExternalUsersShareDialogOpen(true));
		dispatch(setShareItemData(shareItemDataData));
		dispatch(setOpenShareUnshareIntegrationDialog(false));
	};

	const handleRemoveUser = async () => {
		if (!userToBeRemoved) return;

		const foundSharedIntegration = sharedIntegrations.find(
			(sharedIntegration) =>
				sharedIntegration.integration_id === integration.id &&
				sharedIntegration.shared_user_id === userToBeRemoved.auth0_id
		);

		if (!foundSharedIntegration) return;

		const unshareIntegrationResponse = await deleteSharedIntegrations(
			user?.stripeId,
			[{ shared_integration_id: foundSharedIntegration.id }]
		);

		if (unshareIntegrationResponse?.isSuccessful) {
			toast.success(
				`Integration ${integration.integration_name} has been unshared successfully!`
			);
		} else {
			toast.error(
				`Integration ${integration.integration_name} has been unshared unsuccessfully!`
			);
		}

		handleCloseShareUnshareIntegrationDialog();
		setUserToBeRemoved(null);
	};

	useEffect(() => {
		if (!integration) return;

		const sharedUsers = sharedIntegrations
			.filter(
				(sharedIntegration) =>
					sharedIntegration.integration_id === integration.id
			)
			.map((sharedIntegration) => sharedIntegration.shared_user_id);
		const activeUsers = users.filter((u) => u.status === "active");

		const integrationOwner = activeUsers.find(
			(user) => user.auth0_id === integration.create_user_id
		);

		if (integrationOwner) {
			setAvailableShareUsers(
				activeUsers.filter(
					(user) =>
						!sharedUsers.includes(user.auth0_id) &&
						user.auth0_id !== integrationOwner.auth0_id
				)
			);
		} else {
			setAvailableShareUsers(activeUsers);
		}
	}, [sharedIntegrations]);

	return (
		<>
			<SoftBox>
				<SoftBox
					sx={{
						alignItems: "flex-start",
						display: "flex",
						justifyContent: "flex-start",
						padding: "12px 0 12px 40px"
					}}
				>
					<SoftInput
						size="medium"
						placeholder="Search..."
						value={searchName}
						onChange={({ currentTarget }) => {
							setSearchName(currentTarget.value);
						}}
						sx={{
							maxWidth: "380px",
							marginRight: "34px"
						}}
					/>
					{isAllowedAction("share") && (
						<SoftButton
							variant="outlined"
							color="info"
							onClick={handleAddUsers}
							disabled={!isAllowedToShare()}
						>
							add
						</SoftButton>
					)}
				</SoftBox>
				<SoftBox>
					<DataTable
						table={tableData}
						entriesPerPage={{ defaultValue: 25 }}
						hiddenTopButtons
						tableView="list"
						noTopPart
						stayVisible
						tableHeight="clamp(350px, calc(100vh - 130px), calc(100vh - 500px))"
					/>
				</SoftBox>
				<ShareUnshareDialog
					open={openShareUnshareDialog}
					isShareMode
					shareUnshareItem={"Integration"}
					shareUnshareItemName={integration?.integration_name}
					onClose={handleCloseShareUnshareIntegrationDialog}
					users={availableShareUsers}
					onAccept={handleShareIntegration}
					handleOpenExternalUsersShareDialog={
						handleOpenExternalUsersShareDialog
					}
				/>
				<RemoveUserDialog
					isOpen={!!userToBeRemoved}
					user={userToBeRemoved}
					onClose={() => setUserToBeRemoved(null)}
					onAccept={handleRemoveUser}
				/>
			</SoftBox>
		</>
	);
};
